import clsx from 'clsx';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { useUser } from '../../app/session';
import { toast } from '../../components/toasts';
import { Badge, Button, ErrorText, Field, Input, Modal, Select, Spinner } from '../../components/ui';
import { del, errorMessage, get, hasPermission, patch, post, put } from '../../lib/api';
import { dateTimeOf } from '../../lib/format';
import { AgentLinksOverview, AgentView, UserView } from '../../lib/types';

const ROLES = [
  { key: 'operator', label: 'Operador' },
  { key: 'supervisor', label: 'Supervisor' },
  { key: 'admin', label: 'Administrador' },
];

export function UsersAdmin() {
  const me = useUser();
  const qc = useQueryClient();
  const users = useQuery({ queryKey: ['users'], queryFn: () => get<UserView[]>('/users') });
  const agents = useQuery({ queryKey: ['agent-links'], queryFn: () => get<AgentLinksOverview>('/agidesk/agents'), retry: false });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<UserView | null>(null);
  const canUpdate = hasPermission(me, 'user:update');

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['users'] });
    void qc.invalidateQueries({ queryKey: ['agent-links'] });
    void qc.invalidateQueries({ queryKey: ['operators'] });
  };
  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast(ok, 'ok');
      refresh();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };

  const agentList = agents.data?.agents.map((a) => a.agent).filter((a) => a.active) ?? [];
  const agentName = (id: string | null) => (id ? (agents.data?.agents.find((a) => a.agent.id === id)?.agent.name ?? `#${id}`) : null);

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-fg-soft">O agente do Agidesk vinculado é o autor dos comentários e o responsável pelos chamados criados pelo usuário.</p>
        {hasPermission(me, 'user:create') && (
          <Button variant="primary" onClick={() => setCreating(true)}>
            <Plus className="size-4" /> Novo usuário
          </Button>
        )}
      </div>
      {agents.isError && <p className="mb-3 rounded-md bg-warn/10 px-3 py-2 text-sm text-warn">Não foi possível listar os agentes do Agidesk agora: {errorMessage(agents.error)}</p>}
      {agents.data && agents.data.staleLinks.length > 0 && (
        <p className="mb-3 rounded-md bg-warn/10 px-3 py-2 text-sm text-warn">
          Vínculos com agentes inativos: {agents.data.staleLinks.map((s) => s.name).join(', ')}
        </p>
      )}
      {users.isLoading && <Spinner />}
      <ErrorText error={users.error} />
      {users.data && (
        <table className="w-full rounded-lg border border-border bg-panel text-sm">
          <thead className="bg-muted text-left text-xs text-fg-soft">
            <tr>
              <th className="px-3 py-2">Usuário</th>
              <th className="px-3 py-2">Perfil</th>
              <th className="px-3 py-2">Agente no Agidesk</th>
              <th className="px-3 py-2">Transfere de outros</th>
              <th className="px-3 py-2">Ativo</th>
              <th className="px-3 py-2">Último acesso</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {users.data.map((u) => {
              const suggestion = agents.data?.agents.find((a) => a.suggestedUser?.id === u.id && !a.linkedUser);
              return (
                <tr key={u.id} className="border-t border-border">
                  <td className="px-3 py-2">
                    <div className="font-medium">{u.name}</div>
                    <div className="text-xs text-fg-soft">{u.email}</div>
                  </td>
                  <td className="px-3 py-2">{u.roles.map((r) => ROLES.find((x) => x.key === r)?.label ?? r).join(', ')}</td>
                  <td className="px-3 py-2">
                    {canUpdate && agents.data ? (
                      <Select
                        className="h-8 text-xs"
                        value={u.agideskAgentId ?? ''}
                        onChange={(e) =>
                          void act(() => put(`/users/${u.id}/agidesk-agent`, { agentId: e.target.value || null }), e.target.value ? 'Agente vinculado' : 'Vínculo removido')
                        }
                      >
                        <option value="">— sem vínculo —</option>
                        {agentList.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.name}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      (agentName(u.agideskAgentId) ?? <span className="text-fg-soft">—</span>)
                    )}
                    {!u.agideskAgentId && suggestion && canUpdate && (
                      <button className="mt-1 text-xs text-accent hover:underline" onClick={() => void act(() => put(`/users/${u.id}/agidesk-agent`, { agentId: suggestion.agent.id }), 'Agente vinculado')}>
                        Sugestão: {suggestion.agent.name} (mesmo e-mail)
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      disabled={!canUpdate}
                      checked={u.canTransferOthers}
                      onChange={(e) => void act(() => patch(`/users/${u.id}`, { canTransferOthers: e.target.checked }), 'Usuário atualizado')}
                    />
                  </td>
                  <td className="px-3 py-2">
                    {u.id === me.id ? (
                      <Badge tone="ok">você</Badge>
                    ) : (
                      <input type="checkbox" disabled={!canUpdate} checked={u.active} onChange={(e) => void act(() => patch(`/users/${u.id}`, { active: e.target.checked }), e.target.checked ? 'Usuário ativado' : 'Usuário desativado')} />
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-fg-soft">
                    {u.lastLoginAt ? dateTimeOf(u.lastLoginAt) : '—'}
                    {u.lockedUntil && Date.parse(u.lockedUntil) > Date.now() && (
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        <Badge tone="warn">Bloqueado até {dateTimeOf(u.lockedUntil)}</Badge>
                        {canUpdate && (
                          <Button size="sm" onClick={() => void act(() => post(`/users/${u.id}/unlock`, {}), 'Usuário desbloqueado')}>
                            Desbloquear
                          </Button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {canUpdate && (
                      <Button size="sm" variant="ghost" onClick={() => setEditing(u)}>
                        <Pencil className="size-3.5" /> Editar
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {editing && <EditUserModal user={editing} isSelf={editing.id === me.id} onClose={() => setEditing(null)} onSaved={refresh} />}
      {creating && <CreateUserModal agents={agentList} onClose={() => setCreating(false)} onCreated={refresh} />}
    </div>
  );
}

function CreateUserModal({ agents, onClose, onCreated }: { agents: Array<{ id: string; name: string }>; onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'operator', agentId: '', canTransferOthers: false });
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await post('/users', {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        roles: [form.role],
        agideskAgentId: form.agentId || undefined,
        canTransferOthers: form.canTransferOthers,
      });
      toast('Usuário criado', 'ok');
      onCreated();
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      title="Novo usuário"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" loading={saving} onClick={() => void submit()}>
            Criar
          </Button>
        </>
      }
    >
      <Field label="Nome">
        <Input value={form.name} onChange={set('name')} autoFocus />
      </Field>
      <Field label="E-mail">
        <Input type="email" value={form.email} onChange={set('email')} />
      </Field>
      <Field label="Senha inicial" hint="(mínimo de 10 caracteres)">
        <Input type="password" autoComplete="new-password" value={form.password} onChange={set('password')} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Perfil">
          <Select value={form.role} onChange={set('role')}>
            {ROLES.map((r) => (
              <option key={r.key} value={r.key}>
                {r.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Agente no Agidesk">
          <Select value={form.agentId} onChange={set('agentId')}>
            <option value="">— sem vínculo —</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={form.canTransferOthers} onChange={(e) => setForm((f) => ({ ...f, canTransferOthers: e.target.checked }))} />
        Pode transferir atendimentos de outros usuários
      </label>
      <ErrorText error={error} />
    </Modal>
  );
}

/** Editar cadastro: nome, e-mail (login), perfil, ramal e, se preenchida, nova senha. */
function EditUserModal({ user, isSelf, onClose, onSaved }: { user: UserView; isSelf: boolean; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({ name: user.name, email: user.email, role: user.roles[0] ?? 'operator', extension: user.extension ?? '', password: '' });
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const changes: Record<string, unknown> = {};
      if (form.name.trim() !== user.name) changes.name = form.name.trim();
      if (form.email.trim().toLowerCase() !== user.email) changes.email = form.email.trim();
      if (!user.roles.includes(form.role) || user.roles.length !== 1) changes.roles = [form.role];
      if ((form.extension.trim() || null) !== user.extension) changes.extension = form.extension.trim() || null;
      if (Object.keys(changes).length) await patch(`/users/${user.id}`, changes);
      if (form.password) await post(`/users/${user.id}/password`, { password: form.password });
      toast(form.password ? 'Usuário atualizado e senha redefinida (as sessões dele foram encerradas)' : 'Usuário atualizado', 'ok');
      onSaved();
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      title={`Editar: ${user.name}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" loading={saving} onClick={() => void save()} disabled={!form.name.trim() || !form.email.trim()}>
            Salvar
          </Button>
        </>
      }
    >
      <Field label="Nome">
        <Input value={form.name} onChange={set('name')} autoFocus />
      </Field>
      <Field label="E-mail" hint="(é o login)">
        <Input type="email" value={form.email} onChange={set('email')} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Perfil">
          <Select value={form.role} onChange={set('role')} disabled={isSelf && user.roles.includes('admin')}>
            {ROLES.map((r) => (
              <option key={r.key} value={r.key}>
                {r.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Ramais" hint="(telefone físico/softphone; ex.: 100)">
          <Input value={form.extension} onChange={set('extension')} maxLength={20} />
        </Field>
      </div>
      <Field label="Nova senha" hint={isSelf ? '(para a sua, use "Trocar minha senha" no topo)' : '(opcional; mínimo de 10 caracteres)'}>
        <Input type="password" autoComplete="new-password" value={form.password} onChange={set('password')} disabled={isSelf} placeholder={isSelf ? '' : 'Deixe em branco para manter'} />
      </Field>
      <ErrorText error={error} />
      <SipCredentialSection userId={user.id} />
    </Modal>
  );
}

/**
 * Ramal WebRTC (telefone no navegador): número e senha do ramal no FreePBX. A senha é guardada
 * cifrada e nunca volta para a tela da administração; só o próprio usuário a recebe para registrar.
 */
function SipCredentialSection({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['sip-credentials'], queryFn: () => get<Array<{ userId: string; extension: string; updatedAt: string }>>('/telephony/sip-credentials') });
  const current = list.data?.find((c) => c.userId === userId) ?? null;
  const agents = useQuery({ queryKey: ['telephony', 'agents'], queryFn: () => get<AgentView[]>('/telephony/agents') });
  const agent = agents.data?.find((a) => a.userId === userId) ?? null;
  const [extension, setExtension] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setPassword('');
      void qc.invalidateQueries({ queryKey: ['sip-credentials'] });
      toast(ok, 'ok');
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 border-t border-border pt-3">
      <div className="mb-1 text-sm font-semibold">Telefone no navegador (WebRTC)</div>
      <p className="mb-2 text-xs text-fg-soft">
        {current ? `Ramal ${current.extension} configurado (${dateTimeOf(current.updatedAt)}).` : 'Sem ramal WebRTC: o telefone não aparece na tela deste usuário.'} No FreePBX, o
        ramal precisa de "Enable WebRTC defaults".
      </p>
      <div className="grid grid-cols-[7rem_1fr_auto] items-end gap-2">
        <Field label="Ramal">
          <Input value={extension} onChange={(e) => setExtension(e.target.value.replace(/\D/g, ''))} placeholder={current?.extension ?? '2001'} maxLength={6} />
        </Field>
        <Field label="Senha do ramal" hint="(secret no FreePBX)">
          <Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <div className="mb-3 flex gap-1.5">
          <Button
            variant="primary"
            loading={busy}
            disabled={!password || !(extension || current?.extension)}
            onClick={() => void run(() => put(`/telephony/sip-credentials/${userId}`, { extension: extension || current?.extension, password }), 'Ramal WebRTC salvo')}
          >
            Salvar ramal
          </Button>
          {current && (
            <Button disabled={busy} onClick={() => void run(() => del(`/telephony/sip-credentials/${userId}`), 'Ramal WebRTC removido')}>
              Remover
            </Button>
          )}
        </div>
      </div>
      <label className={clsx('mt-1 flex items-center gap-2 text-sm', !agent && 'text-fg-soft')}>
        <input
          type="checkbox"
          checked={agent?.answersQueue ?? true}
          disabled={!agent || busy}
          onChange={(e) =>
            void run(async () => {
              await put(`/telephony/agents/${userId}/queue`, { answersQueue: e.target.checked });
              void qc.invalidateQueries({ queryKey: ['telephony'] });
            }, e.target.checked ? 'Passa a atender a fila de ligações' : 'Não atende mais a fila de ligações')
          }
        />
        Atende a fila de ligações
        <span className="text-xs text-fg-soft">{agent ? '(desmarcado: tem telefone, mas a fila não toca para ele)' : '(cadastre um ramal primeiro)'}</span>
      </label>
      <ErrorText error={error} />
    </div>
  );
}
